import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {EquipmentData, EquipmentIdType} from '../types'

const ITEMS_PER_RESPONSE = 100

type Payload = {
  searchTerm?: string
  plantId: string
  idType?: EquipmentIdType
}

interface UseMainEquipmentNumberSearchOptions<T> {
  enabled?: boolean
  select?: (data: EquipmentData[]) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useMainEquipmentNumberSearchQuery = <T = EquipmentData[],>(
  {searchTerm, idType, plantId}: Payload,
  options?: UseMainEquipmentNumberSearchOptions<T>
) => {
  const {
    i18n: {language}
  } = useTranslation()

  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchMainEquipmentNumbers = useCallback(async () => {
    if (!plantId) return

    setIsLoading(true)

    try {
      const path = `/plantconfig/${plantId}/mainequipmentnumbers`
      const params = {
        params: {
          searchPattern: searchTerm,
          itemsPerResponse: ITEMS_PER_RESPONSE,
          language,
          idType
        }
      }

      const response = await ApiClient.axiosInstance.get<EquipmentData[]>(path, params)
      const result = select ? select(response.data) : (response.data as T)

      setData(result)
      setError(null)
      onSuccess?.(result)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err)
        onError?.(err)
      } else {
        console.error('Unexpected error:', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, searchTerm, idType, language, select, onSuccess, onError])

  useEffect(() => {
    if (enabled && plantId) {
      void fetchMainEquipmentNumbers()
    }
  }, [enabled, fetchMainEquipmentNumbers, plantId])

  return {
    data,
    error,
    isLoading,
    refetch: fetchMainEquipmentNumbers
  }
}
